import { useContext, useMemo } from 'react';

import { faBadgeCheck } from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import { Alert, AlertTitle, Skeleton } from '@repo/videa-components';

import { useLocalizedFormatDate } from '@/modules/claims/freeClaims/state';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { ExternalPatientContext } from '../constants';
import { getTimeRemaining } from '../FreeTrialBanner';
import { TrialStatus } from '../types';

import {
  ManageSubscriptionButton,
  ManageSubscriptionView,
} from './ManageSubscriptionView';

interface Props {
  isLoading?: boolean;
  onClickCancelSubscription: () => void;
  onClose: () => void;
  paidStartedOn?: string;
  trialEndsOn?: string;
  trialStartedOn?: string;
  trialStatus?: TrialStatus;
}

export const SubscriptionDetails = ({
  isLoading,
  onClickCancelSubscription,
  onClose,
  paidStartedOn,
  trialEndsOn,
  trialStartedOn,
  trialStatus,
}: Props) => {
  const { t } = useLingui();
  const { trackEvent } = useTrackEvent();
  const formatDate = useLocalizedFormatDate();
  const externalPatientContext = useContext(ExternalPatientContext);
  const { days } = useMemo(() => getTimeRemaining(trialEndsOn), [trialEndsOn]);

  const isFreeTrial = trialStatus === TrialStatus.TRIAL;
  // For `isRequestCancellation`, we're not checking CANCEL_REQUEST_TRIAL
  // because when the free trial is cancelled the user is shown the trial
  // expired view, which doesn't have access to the account menu. We're not
  // checking CANCEL_REQUEST_PAID because our endpoints return 403s and the
  // panel doesn't show.
  const isRequestCancellation =
    trialStatus === TrialStatus.CANCEL_REQUEST_PROVISIONAL;

  const getPlan = () => {
    switch (trialStatus) {
      case TrialStatus.TRIAL:
        return t`Detect AI Trial`;
      case TrialStatus.PAID:
      case TrialStatus.PAID_BUT_PROVISIONAL:
        return t`Detect AI`;
      default:
        return t`Unknown`;
    }
  };

  const getSinceDate = () => {
    switch (trialStatus) {
      case TrialStatus.TRIAL:
        return trialStartedOn
          ? formatDate(trialStartedOn, false, 'PP')
          : t`Unknown`;
      case TrialStatus.PAID:
        return paidStartedOn
          ? formatDate(paidStartedOn, false, 'PP')
          : t`Unknown`;
      default:
        return t`Unknown`;
    }
  };

  return (
    <ManageSubscriptionView
      // TODO: Hide cancellation flow for now
      // footer={
      //   <ManageSubscriptionButton
      //     onClick={onClickCancelSubscription}
      //     label={isFreeTrial ? t`Cancel Trial` : t`Cancel Subscription`}
      //   />
      // }
      onClose={onClose}
      title={t`Manage Subscription`}
    >
      {isLoading ? (
        <div className="vai-mt-4 vai-flex vai-flex-col vai-gap-2">
          <Skeleton className="vai-h-4 vai-w-40" />
          <Skeleton className="vai-h-4 vai-w-40" />
        </div>
      ) : (
        <div className="vai-mt-4 vai-ml-1 vai-grid vai-grid-cols-[auto_auto_1fr] vai-gap-2 vai-items-center vai-text-white">
          <FontAwesomeIcon icon={faBadgeCheck} />
          <p className="vai-font-inter vai-text-sm vai-font-medium">
            <Trans>Plan:</Trans>
          </p>
          <p className="vai-font-inter vai-text-sm vai-font-medium">
            {getPlan()}
          </p>
          <FontAwesomeIcon icon={faCalendar} />
          <p className="vai-font-inter vai-text-sm vai-font-medium">
            <Trans>Since:</Trans>
          </p>
          <p className="vai-font-inter vai-text-sm vai-font-medium">
            {getSinceDate()}
          </p>
        </div>
      )}

      {isRequestCancellation && (
        <Alert variant="successSolid">
          <AlertTitle className="vai-text-sm !vai-leading-5">
            <Trans>
              Your subscription will expire at the end of the monthly billing
              period
            </Trans>
          </AlertTitle>
        </Alert>
      )}

      {/* TODO: Temporary copy while full cancellation flow is not supported */}
      <p className="vai-font-inter vai-text-sm vai-text-zinc-400">
        {isFreeTrial ? (
          <Trans>
            Your free trial ends in {days} days. To cancel earlier, contact
            support at 1-800-DENTRIX (336-8749).
          </Trans>
        ) : (
          <Trans>
            To cancel your subscription, contact support at 1-800-DENTRIX
            (336-8749).
          </Trans>
        )}
      </p>
    </ManageSubscriptionView>
  );
};
